import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { trim } from "lodash";
import {
  H1,
  Flex,
  FlexItem,
  Button,
  Icon,
  TextInput,
  Notification,
  Popover,
  Loading,
} from "orcs-design-system";
import GroupPropType from "src/custom-prop-types/group";

import {
  ILLEGAL_TEAM_NAME_CHARACTERS,
  MAX_TEAM_NAME_LENGTH,
  MIN_TEAM_NAME_LENGTH,
} from "src/consts/global";
import useUserRole from "src/app/Restricted/useUserRole";
import { ADMIN_ROLE, POWER_ROLE } from "src/services/auth";

// TODO: Find out a comfortable input width

const validateNewName = (newName) => {
  if (!newName || newName.length < MIN_TEAM_NAME_LENGTH) {
    return "Minimum team name length is 3 characters excluding leading and trailing spaces.";
  }

  return null;
};

// eslint-disable-next-line react/display-name
const ErrorNotification = React.memo(({ error }) => {
  if (!error) {
    return null;
  }

  return (
    <Notification
      floating
      centered
      colour="danger"
      icon={["far", "exclamation-circle"]}
    >
      {error}
    </Notification>
  );
});

ErrorNotification.propTypes = {
  error: PropTypes.string,
};

// eslint-disable-next-line react/display-name
const TeamName = React.memo(({ isEditMode, name, color = "white" }) => {
  if (isEditMode) {
    return null;
  }

  return (
    <H1
      color={color}
      fontWeight="600"
      fontSize="2.4rem"
      className="guided-tour-team-details-team-name"
    >
      {name}
    </H1>
  );
});

TeamName.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  color: PropTypes.string,
};

// eslint-disable-next-line react/display-name
const EditBtn = React.memo(({ isEditMode, onEdit, disabled, popoverText }) => {
  if (isEditMode || disabled) {
    return null;
  }

  return (
    <Popover
      text={popoverText}
      width="fit-content"
      tabIndex="-1"
      direction="top"
      mt="1px"
    >
      <Button
        dataTestId="edit-btn"
        iconOnly
        onClick={onEdit}
        height="22px"
        width="22px"
        borderRadius="22px"
        variant="borderGrey"
        bg="white"
        ariaLabel={popoverText}
      >
        <Icon icon={["fas", "pencil"]} color="greyDarkest" />
      </Button>
    </Popover>
  );
});

EditBtn.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  popoverText: PropTypes.string,
};

// eslint-disable-next-line react/display-name
const ConfirmAndCancelBtns = React.memo(
  ({ isEditMode, onSave, saveDisabled, onCancel, canCancel, isLoading }) => {
    if (!isEditMode) {
      return null;
    }

    return (
      <>
        <Button
          dataTestId="save-btn"
          iconOnly
          ariaLabel="Save team name"
          variant={saveDisabled || isLoading ? "disabled" : "successAlternate"}
          onClick={onSave}
          height="28px"
          width="28px"
          borderRadius="28px"
          disabled={saveDisabled || isLoading}
        >
          {isLoading ? <Loading /> : <Icon icon={["fas", "check"]} />}
        </Button>
        {canCancel && !isLoading && (
          <Button
            dataTestId="cancel-btn"
            small
            iconOnly
            ariaLabel="Cancel"
            variant="dangerAlternate"
            onClick={onCancel}
            height="28px"
            width="28px"
            borderRadius="28px"
          >
            <Icon icon={["fas", "times"]} />
          </Button>
        )}
      </>
    );
  }
);

ConfirmAndCancelBtns.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  canCancel: PropTypes.bool.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const TeamNameInput = ({
  isEditMode,
  value,
  onChange,
  onSave,
  onCancel,
  isLoading,
}) => {
  const handleFocus = (e) => e.target.select(); // auto select text
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSave();
    }
    if (e.key === "Escape") {
      onCancel();
    }
  };

  if (!isEditMode) {
    return null;
  }

  return (
    <FlexItem width="250px">
      <TextInput
        id="team-name-edit"
        autoFocus
        fullWidth
        height="28px"
        data-testid="team-name-editor-input"
        ariaLabel="Team name"
        className="guided-tour-team-details-team-name"
        disabled={isLoading}
        maxLength={MAX_TEAM_NAME_LENGTH}
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
    </FlexItem>
  );
};

TeamNameInput.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const TeamNameEditor = ({
  team,
  updateName,
  isEditMode = false,
  canCancel = true,
  autoExitEditMode = true,
  color = "white",
}) => {
  const { name } = team;

  const [editMode, setEditMode] = useState(isEditMode);
  const [isLoading, setLoading] = useState(false);
  const [newName, setNewName] = useState(name);
  const [error, setError] = useState(null);

  const userRole = useUserRole();

  const editDisabled = useMemo(
    () => userRole !== POWER_ROLE && userRole !== ADMIN_ROLE,
    [userRole]
  );

  useEffect(() => {
    setNewName(name);
  }, [name]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setError(null);
    }, 5000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
    };
  }, [error]);

  const onEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const onSave = useCallback(async () => {
    const proposedName = trim(newName);

    if (proposedName === name) {
      return;
    }

    const result = validateNewName(proposedName);

    if (result) {
      setError(result);
      return;
    }

    try {
      setLoading(true);
      await updateName(proposedName);
      // TODO: Update team name immediately?
      setLoading(false);
    } catch (err) {
      setError(err.toString());
      setLoading(false);
    } finally {
      if (autoExitEditMode) {
        setEditMode(false);
      }
    }
  }, [newName, name, updateName, autoExitEditMode]);

  const onCancel = useCallback(() => {
    setNewName(name);
    setEditMode(false);
    setError(null);
  }, [name]);

  const onNameChange = useCallback((e) => {
    const proposedName = e.target.value.replace(
      ILLEGAL_TEAM_NAME_CHARACTERS,
      ""
    );

    setNewName(proposedName);
  }, []);

  const onDismiss = useCallback(() => {
    setError(null);
  }, []);

  const popoverText = !editDisabled
    ? "Change team name"
    : "Your current access level does not allow you to edit team names";

  return (
    <Flex alignItems="center" gap="s">
      <TeamName isEditMode={editMode} name={name} color={color} />
      <TeamNameInput
        isEditMode={editMode}
        isLoading={isLoading}
        value={newName}
        onChange={onNameChange}
        onSave={onSave}
        onCancel={onCancel}
      />
      <EditBtn
        isEditMode={editMode}
        onEdit={onEdit}
        disabled={editDisabled}
        popoverText={popoverText}
      />
      <ConfirmAndCancelBtns
        isEditMode={editMode}
        isLoading={isLoading}
        canCancel={canCancel}
        saveDisabled={newName === name}
        onSave={onSave}
        onCancel={onCancel}
      />
      <ErrorNotification error={error} onDismiss={onDismiss} />
    </Flex>
  );
};

TeamNameEditor.propTypes = {
  team: GroupPropType.isRequired,
  updateName: PropTypes.func,
  isEditMode: PropTypes.bool,
  canCancel: PropTypes.bool,
  autoExitEditMode: PropTypes.bool,
  color: PropTypes.string,
};

export default TeamNameEditor;
